import { useState } from "react";
import { Link } from "wouter";
import {
  GraduationCap,
  Plus,
  CreditCard,
  Download,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  RotateCcw,
  AlertTriangle,
  ArrowRight,
  Loader2,
  UserCog,
  CalendarDays,
} from "lucide-react";
import { useCurrentUser } from "@/lib/auth";
import { useMyEleves, useMyPaiements, useMyDemandes } from "@/lib/backoffice-hooks";
import { downloadRecuPdf } from "@/lib/downloadRecu";
import { formatXof, formatDate } from "@/lib/format";
import type { MyEleve, MyPaiementRow, MyDemande } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Eyebrow, Pastille, SolidCard } from "@/components/finances/primitives";
import AddChildDialog from "@/components/parent/AddChildDialog";
import ChildDetailDialog from "@/components/parent/ChildDetailDialog";
import ProfilDialog from "@/components/parent/ProfilDialog";
import RecuPreviewDialog from "@/components/RecuPreviewDialog";

const STATUS_MAP: Record<
  string,
  { label: string; color: string; Icon: typeof CheckCircle2 }
> = {
  SUCCESS: { label: "Payé", color: "text-green-700 bg-green-50", Icon: CheckCircle2 },
  VALIDE: { label: "Payé", color: "text-green-700 bg-green-50", Icon: CheckCircle2 },
  PROCESSING: { label: "En cours", color: "text-gold bg-gold/10", Icon: Clock },
  PENDING: { label: "En attente", color: "text-gold bg-gold/10", Icon: Clock },
  EN_ATTENTE: { label: "En attente", color: "text-gold bg-gold/10", Icon: Clock },
  FAILED: { label: "Échec", color: "text-red-600 bg-red-50", Icon: XCircle },
  REJETE: { label: "Échec", color: "text-red-600 bg-red-50", Icon: XCircle },
  CANCELLED: { label: "Annulé", color: "text-navy/60 bg-navy/5", Icon: XCircle },
  ANNULE: { label: "Annulé", color: "text-navy/60 bg-navy/5", Icon: XCircle },
  REFUNDED: { label: "Remboursé", color: "text-blue-700 bg-blue-50", Icon: RotateCcw },
  REMBOURSE: { label: "Remboursé", color: "text-blue-700 bg-blue-50", Icon: RotateCcw },
};

function statusStyle(statut: string) {
  return STATUS_MAP[statut.toUpperCase()] ?? STATUS_MAP.PENDING;
}

function ChildCard({ child, paiements }: { child: MyEleve; paiements: MyPaiementRow[] }) {
  const [detailOpen, setDetailOpen] = useState(false);
  const initiales =
    `${child.prenoms?.[0] ?? ""}${child.nom?.[0] ?? ""}`.toUpperCase() || "?";
  return (
    <div className="bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] p-5 flex flex-col">
      <div className="flex items-start gap-3 mb-4">
        <div
          className="w-12 h-12 rounded-full bg-navy flex items-center justify-center shrink-0 text-white font-bold text-[14px]"
          aria-hidden
        >
          {initiales}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-bold text-navy truncate">
              {child.prenoms} {child.nom}
            </h3>
            <Pastille variant="success" className="shrink-0 font-bold">
              <CheckCircle2 size={12} className="mr-1" /> Lié
            </Pastille>
          </div>
          {child.ecoleNom && (
            <p className="text-sm text-navy/70 truncate">
              {child.ecoleNom}
            </p>
          )}
          {/* Le libellé de la classe, jamais son identifiant technique. */}
          {child.classeLibelle && (
            <p className="text-sm text-navy/60 truncate">Classe : {child.classeLibelle}</p>
          )}
          {child.matricule && (
            <p className="text-xs text-navy/45 truncate">Matricule : {child.matricule}</p>
          )}
          {child.anneeScolaire ? (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-navy/5 px-2 py-0.5 text-xs font-bold text-navy/70">
              <CalendarDays size={12} /> Année : {child.anneeScolaire}
            </span>
          ) : (
            <p className="mt-1 text-xs italic text-navy/40 truncate">
              En attente d'inscription
            </p>
          )}
          {child.resteAPayer != null && (
            <p className="mt-1 text-sm font-bold text-[#B8881F]">
              Reste à payer : {formatXof(child.resteAPayer)}
            </p>
          )}
        </div>
      </div>
      <div className="mt-auto flex flex-col gap-2 pt-2">
        <button
          type="button"
          onClick={() => setDetailOpen(true)}
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] border-[1.5px] border-navy bg-white px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:bg-navy/[0.06]"
        >
          <Eye size={16} /> Voir le détail
        </button>
        <Link
          href={`/payer?ecoleId=${child.etablissementId}&eleveId=${child.id}`}
          className="inline-flex w-full items-center justify-center gap-2 rounded-[14px] bg-navy px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-[#13204A] hover:-translate-y-px hover:shadow-[0_10px_18px_rgba(19,32,74,0.22)]"
        >
          <CreditCard size={16} /> Payer
        </Link>
      </div>

      <ChildDetailDialog
        open={detailOpen}
        onOpenChange={setDetailOpen}
        child={child}
        paiements={paiements}
      />
    </div>
  );
}

function PaiementRow({ p }: { p: MyPaiementRow }) {
  const [downloading, setDownloading] = useState(false);
  const [apercuOpen, setApercuOpen] = useState(false);
  const st = statusStyle(p.statut);
  const isSuccess = p.statut.toUpperCase() === "SUCCESS";

  async function handleDownload() {
    setDownloading(true);
    try {
      await downloadRecuPdf(p.id, p.numeroPaiement);
    } catch {
      // l'erreur est déjà remontée via toast dans downloadRecuPdf
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-4 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${st.color}`}>
          <st.Icon size={20} />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-navy truncate">{p.motifLabel}</p>
          <p className="text-sm text-navy/60 truncate">
            {p.ecoleDenomination} · {p.eleveNom}
          </p>
          <p className="text-xs text-navy/45">
            {formatDate(p.datePaiement ?? p.creeLe)} · {p.numeroPaiement}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-4 sm:flex-col sm:items-end sm:justify-center">
        <div className="text-right">
          <p className="font-bold text-navy">{formatXof(p.montant)}</p>
          <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${st.color}`}>
            {st.label}
          </span>
        </div>
        {isSuccess && (
          <div className="inline-flex items-center gap-3">
            <button
              type="button"
              onClick={() => setApercuOpen(true)}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-navy/70 hover:underline"
              data-testid={`button-apercu-${p.numeroPaiement}`}
            >
              <Eye size={15} /> Aperçu
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="inline-flex items-center gap-1.5 text-sm font-bold text-coral hover:underline disabled:opacity-50"
            >
              {downloading ? (
                <Loader2 size={15} className="animate-spin" />
              ) : (
                <Download size={15} />
              )}
              Reçu
            </button>
          </div>
        )}
      </div>

      {isSuccess && (
        <RecuPreviewDialog
          open={apercuOpen}
          onOpenChange={setApercuOpen}
          source={{ kind: "auth", paiementId: p.id }}
          reference={p.numeroPaiement}
        />
      )}
    </div>
  );
}

const DEMANDE_STATUS_MAP: Record<
  string,
  { label: string; color: string; Icon: typeof CheckCircle2 }
> = {
  // Valeurs RÉELLES du backend (RattachementDemande.Statut, V923) :
  PENDING: { label: "En attente", color: "text-gold bg-gold/10", Icon: Clock },
  ACCEPTED: { label: "Validée", color: "text-green-700 bg-green-50", Icon: CheckCircle2 },
  REJECTED: { label: "Refusée", color: "text-red-600 bg-red-50", Icon: XCircle },
};

function demandeStatusStyle(statut: string) {
  return DEMANDE_STATUS_MAP[statut.toUpperCase()] ?? DEMANDE_STATUS_MAP.PENDING;
}

function DemandeRow({ d }: { d: MyDemande }) {
  const st = demandeStatusStyle(d.statut);
  return (
    <div className="bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] p-5 flex items-center justify-between gap-4">
      <div className="flex items-start gap-4 min-w-0">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${st.color}`}>
          <st.Icon size={20} />
        </div>
        <div className="min-w-0">
          <p className="font-bold text-navy truncate">
            {d.elevePrenoms} {d.eleveNom}
          </p>
          {d.ecoleNom && (
            <p className="text-sm text-navy/60 truncate">{d.ecoleNom}</p>
          )}
          <p className="text-xs text-navy/45">{formatDate(d.creeLe)}</p>
        </div>
      </div>
      <span className={`shrink-0 inline-block text-xs px-2 py-0.5 rounded-full ${st.color}`}>
        {st.label}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useCurrentUser();
  const [addOpen, setAddOpen] = useState(false);
  const [profilOpen, setProfilOpen] = useState(false);

  const eleves = useMyEleves();
  const paiements = useMyPaiements({ page: 0, size: 50 });
  const demandes = useMyDemandes();

  const children = eleves.data ?? [];
  const rows = paiements.data?.items ?? [];
  const demandesRows = demandes.data ?? [];

  // Héros « À régler » (langage éditorial V05) — un seul chiffre, tiré de
  // l'agrégat SERVEUR (resteAPayer par enfant, EleveSoldeService : année
  // active, paiements hors-système inclus). Pas de « couverture % » : la
  // recomposer côté client depuis /me/paiements serait fausse (page 1
  // seulement, toutes années, sans les paiements espèces — revue 2026-07-13).
  const totalReste = children.reduce((s, c) => s + (c.resteAPayer ?? 0), 0);
  const heroChild = children.find((c) => (c.resteAPayer ?? 0) > 0);
  const enfantsAvecReste = children.filter((c) => (c.resteAPayer ?? 0) > 0).length;

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <Eyebrow>Espace famille</Eyebrow>
          <h1 className="mt-1 mb-2 text-[28px] sm:text-[34px] leading-tight [font-family:'Cormorant_Garamond',serif] font-bold text-navy">
            Bonjour {user?.prenoms ?? ""}
          </h1>
          <p className="text-navy/70">
            Gérez la scolarité de vos enfants et suivez vos paiements.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setProfilOpen(true)}>
          <UserCog size={16} /> Mon profil
        </Button>
      </header>

      {/* ---------- Héros « À régler » ---------- */}
      {totalReste > 0 && heroChild && (
        <SolidCard tone="navy" className="relative overflow-hidden">
          <div
            aria-hidden
            className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-[#E6B547]/15 blur-2xl pointer-events-none"
          />
          <div className="relative">
            <Eyebrow tone="white">À régler</Eyebrow>
            <p className="mt-3 text-[44px] sm:text-[56px] leading-[1.05] [font-family:'Cormorant_Garamond',serif] font-bold text-white">
              {formatXof(totalReste)}
            </p>
            <p className="mt-2 text-white/85 text-[14px]">
              Solde restant sur la scolarité de {enfantsAvecReste > 1 ? `${enfantsAvecReste} enfants` : "votre enfant"} (année en cours).
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                href={`/payer?ecoleId=${heroChild.etablissementId}&eleveId=${heroChild.id}`}
                className="inline-flex items-center justify-center gap-2 rounded-[14px] font-semibold h-[56px] px-6 text-[16px] bg-[#E6B547] text-[#1C1C1C] hover:bg-[#D4A038] transition-all hover:-translate-y-px hover:shadow-[0_10px_18px_rgba(212,160,56,0.28)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#E6B547]/35"
              >
                Payer maintenant
                <ArrowRight size={16} />
              </Link>
              <a
                href="#historique"
                className="inline-flex items-center justify-center gap-2 rounded-[14px] font-semibold h-[56px] px-6 text-[16px] text-white bg-white/10 hover:bg-white/20 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[#E6B547]/35"
              >
                Voir l'historique
              </a>
            </div>
          </div>
        </SolidCard>
      )}

      {/* ---------- Mes enfants ---------- */}
      <section>
        <div className="flex items-center justify-between mb-6">
          <h2 className="[font-family:'Outfit',sans-serif] font-semibold uppercase text-[11px] tracking-[1.6px] text-[#B8881F]">
            Mes enfants
          </h2>
          {children.length > 0 && (
            <Button size="sm" onClick={() => setAddOpen(true)}>
              <Plus size={16} /> Ajouter un enfant
            </Button>
          )}
        </div>

        {eleves.isLoading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-40 bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] animate-pulse"
              />
            ))}
          </div>
        ) : eleves.error ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex items-start gap-3 text-red-700">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <p className="text-sm">
              Impossible de charger vos enfants pour le moment. Veuillez réessayer.
            </p>
          </div>
        ) : children.length === 0 ? (
          <div className="bg-cream rounded-2xl p-12 text-center border border-dashed border-orange-100">
            <GraduationCap size={48} className="mx-auto text-navy/30 mb-4" />
            <h3 className="text-lg font-bold text-navy mb-2">Aucun enfant rattaché</h3>
            <p className="text-navy/60 mb-6 max-w-md mx-auto">
              Ajoutez vos enfants pour suivre leur scolarité, ou payez directement
              leurs frais scolaires.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => setAddOpen(true)}>
                <Plus size={18} /> Ajouter un enfant
              </Button>
              <Link
                href="/ecoles"
                className="inline-flex items-center gap-2 rounded-full border border-navy/20 bg-white px-6 py-3 text-sm font-bold text-navy transition-colors hover:bg-navy/5"
              >
                <CreditCard size={16} /> Effectuer un paiement
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map((child) => (
              <ChildCard key={child.id} child={child} paiements={rows} />
            ))}
          </div>
        )}
      </section>

      {/* ---------- Demandes de rattachement ---------- */}
      {demandesRows.length > 0 && (
        <section>
          <h2 className="[font-family:'Outfit',sans-serif] font-semibold uppercase text-[11px] tracking-[1.6px] text-[#B8881F] mb-6">
            Demandes de rattachement
          </h2>
          <div className="space-y-4">
            {demandesRows.map((d) => (
              <DemandeRow key={d.id} d={d} />
            ))}
          </div>
        </section>
      )}

      {/* ---------- Historique des paiements ---------- */}
      <section id="historique">
        <h2 className="[font-family:'Outfit',sans-serif] font-semibold uppercase text-[11px] tracking-[1.6px] text-[#B8881F] mb-6">
          Historique des paiements
        </h2>

        {paiements.isLoading ? (
          <div className="space-y-4">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-24 bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] animate-pulse"
              />
            ))}
          </div>
        ) : paiements.error ? (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-6 flex items-start gap-3 text-red-700">
            <AlertTriangle size={20} className="shrink-0 mt-0.5" />
            <p className="text-sm">
              Impossible de charger l'historique des paiements. Veuillez réessayer.
            </p>
          </div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-[18px] border border-[#E5E5E5] shadow-[0_6px_16px_rgba(27,42,94,0.06)] p-8 text-center text-navy/60">
            Aucun paiement enregistré pour le moment. Vos paiements apparaîtront
            ici une fois effectués.
          </div>
        ) : (
          <div className="space-y-4">
            {rows.map((p) => (
              <PaiementRow key={p.id} p={p} />
            ))}
          </div>
        )}
      </section>

      <AddChildDialog open={addOpen} onOpenChange={setAddOpen} />
      <ProfilDialog open={profilOpen} onOpenChange={setProfilOpen} />
    </div>
  );
}
